import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faFolder } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiCreateCategoryResponseBody } from '~/lib/api/modules/Categories';
import type { IUseCreateCategoryMutationValues } from '~/lib/api/modules/Categories/hooks/useCreateCategoryMutation';
import type { ICategoryFormValues } from './CategoryForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useCreateCategoryMutation } from '~/lib/api/modules/Categories/hooks/useCreateCategoryMutation';
import { CategoryForm } from './CategoryForm';

export type ICreateCategoryFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiCreateCategoryResponseBody,
    inputValues: IUseCreateCategoryMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  parentCategoryId?: string;
};

export const CreateCategoryForm: React.FC<ICreateCategoryFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, appId, parentCategoryId } = props;
  const { t } = useTranslation();
  const createCategoryMutation = useCreateCategoryMutation();
  const [isDraft, setIsDraft] = useState(false);

  const handleSubmit: SubmitHandler<ICategoryFormValues> = (values) => {
    if (createCategoryMutation.isLoading) {
      return;
    }

    return createCategoryMutation.mutateAsync(
      {
        appId,
        body: {
          name: values.name ?? '',
          picture: values.pictures?.length ? [values.pictures[0].id] : [],
          parentId: values.parentId ? values.parentId : undefined,
          action: '',
          hidden: values.isDraft ?? false,
          isEvent: values.isEvent,
          reversedFlow: false,
          badges: values.userBadges,
          badgesAllow: values.requireAllUserBadges ? 'all' : 'any',
          forcedAuthor: values.authorName,
          rssFeedUrl: values.rssFeedUrl,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={isDraft} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faFolder} />}>
        {t('CreateCategoryForm.title')}{' '}
        {isDraft ? t('CreateCategoryForm.not-published-yet') : null}
      </ContentBlock.Title>

      <CategoryForm
        mode="creation"
        appId={appId}
        cancelUrl={cancelUrl}
        defaultValues={{ parentId: parentCategoryId }}
        onSubmit={handleSubmit}
        onDraftStatusChange={setIsDraft}
      />
    </ContentBlock>
  );
};
